import React, { useState, useRef, useEffect } from 'react';
import { Send, X, MessageSquare } from 'lucide-react';

/**
 * FloatingChatInput Component
 * A floating chat input that appears on double-click
 * 
 * @param {Object} props
 * @param {number} props.x - X position in pixels
 * @param {number} props.y - Y position in pixels
 * @param {Function} props.onSend - Callback when message is sent (receives message)
 * @param {Function} props.onClose - Callback when input is closed
 * @param {string} props.defaultMessage - Context information (will be added to user message)
 */
export default function FloatingChatInput({ 
  x, 
  y, 
  onSend, 
  onClose,
  defaultMessage = ''
}) {
  const [message, setMessage] = useState('');
  const contextMessage = defaultMessage; // Store context separately
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Auto-focus when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle clicks outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        // Close if clicking outside the container
        onClose();
      }
    };

    // Add a small delay to avoid immediate closing on the double-click event
    const timeout = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSend = () => {
    // Always send context, add user message if provided
    if (onSend) {
      // If user typed a message, combine with context
      // Otherwise just send the context
      const finalMessage = message.trim() 
        ? `${contextMessage}\n\n${message.trim()}`
        : contextMessage;
      onSend(finalMessage);
      setMessage('');
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Calculate position to keep input within viewport
  const getAdjustedPosition = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const inputWidth = 320; // Approximate width
    const inputHeight = 150; // Approximate height
    
    let adjustedX = x;
    let adjustedY = y;

    // Adjust X to keep within viewport
    if (adjustedX + inputWidth > viewportWidth) {
      adjustedX = viewportWidth - inputWidth - 20;
    }
    if (adjustedX < 20) {
      adjustedX = 20;
    }

    // Adjust Y to keep within viewport
    if (adjustedY + inputHeight > viewportHeight) {
      adjustedY = viewportHeight - inputHeight - 20;
    }
    if (adjustedY < 20) {
      adjustedY = 20;
    }

    return { x: adjustedX, y: adjustedY };
  };

  const position = getAdjustedPosition();

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .floating-chat-input {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
      <div
        ref={containerRef}
        className="floating-chat-input fixed z-50 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 min-w-[300px] max-w-[400px]"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-700">Quick Edit</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          type="button"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Context Info */}
      {contextMessage && (
        <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          <span className="font-semibold">Component:</span> {contextMessage.split('\n')[0].replace('Regarding the ', '').replace(' component', '')}
        </div>
      )}
      
      {/* Input */}
      <textarea
        ref={inputRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Describe your changes to this component..."
        className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        rows="3"
      />

      {/* Footer */}
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-gray-500">
          Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> to send
        </p>
        <button
          onClick={handleSend}
          className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-sm hover:shadow-md"
          type="button"
        >
          <Send className="h-3.5 w-3.5" />
          <span>Send</span>
        </button>
      </div>
    </div>
    </>
  );
}
